import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Home from "./pages/Home";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Inbox from "./pages/Inbox";
import Review from "./pages/Review";
import Segments from "./pages/Segments";
import Campaigns from "./pages/Campaigns";
import Marketing from "./pages/Marketing";
import Referrals from "./pages/Referrals";
import Ads from "./pages/Ads";
import Settings from "./pages/Settings";

/**
 * Session gate + router. Unauthenticated users only ever see the login page
 * (email + password, signups disabled). Signing out anywhere flips the session
 * to null via onAuthStateChange and lands back here.
 */
export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data }) => {
        setSession(data.session);
        setReady(true);
      })
      .catch(() => setReady(true));

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready) {
    return (
      <div className="app-splash">
        <div className="app-splash-brand">
          Fresh IV <span className="brand-accent">CRM</span>
        </div>
      </div>
    );
  }

  if (!session) return <Login />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="review" element={<Review />} />
          <Route path="segments" element={<Segments />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="marketing" element={<Marketing />} />
          <Route path="referrals" element={<Referrals />} />
          <Route path="ads" element={<Ads />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
